import React, { useEffect, useRef, useState } from "react";
import { FaPlus, FaTrash, FaImage, FaTimes, FaUpload } from "react-icons/fa";

import AOS from "aos";
import "aos/dist/aos.css";

import "./About.css";
import { BASE_URL } from "../../Config/config";

export default function About() {
  const fileInputRef = useRef(null);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState("");

  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  /* =====================================================
     FETCH ABOUT IMAGES
  ===================================================== */

  useEffect(() => {
    fetchAboutImages();
  }, []);

  const fetchAboutImages = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/about.php?action=get`);

      if (!response.ok) {
        throw new Error(`About API returned ${response.status}`);
      }

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid About API Response:", text);
        setImages([]);
        return;
      }

      if (result.status && Array.isArray(result.data)) {
        setImages(result.data);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error("Fetch About Images Error:", error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    AOS.init({
      duration: 700,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);
  /* =====================================================
     IMAGE URL
  ===================================================== */

  const getImageUrl = (image) => {
    if (!image) return "";

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =====================================================
     OPEN ADD FORM
  ===================================================== */

  const openAddForm = () => {
    setSelectedFile(null);
    setPreview("");
    setShowAddForm(true);
  };

  /* =====================================================
     CLOSE ADD FORM
  ===================================================== */

  const closeAddForm = () => {
    if (uploading) return;

    setShowAddForm(false);
    setSelectedFile(null);
    setPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* =====================================================
     FILE SELECT
  ===================================================== */

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      setPreview("");
      return;
    }

    /* Allowed image types */

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, JPEG, PNG and WEBP images are allowed.");

      e.target.value = "";
      setSelectedFile(null);
      setPreview("");

      return;
    }

    /* File size validation - 10 MB */

    if (file.size > 10 * 1024 * 1024) {
      alert("Image size must be less than 10 MB.");

      e.target.value = "";
      setSelectedFile(null);
      setPreview("");

      return;
    }

    setSelectedFile(file);

    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
  };

  /* =====================================================
     UPLOAD IMAGE
  ===================================================== */

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      alert("Please select an image.");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();

      formData.append("image", selectedFile);

      const response = await fetch(`${BASE_URL}/about.php?action=add`, {
        method: "POST",
        body: formData,
      });

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid Upload Response:", text);

        alert("Invalid server response.");
        return;
      }

      if (!result.status) {
        alert(result.message || "Failed to upload image.");
        return;
      }

      alert("About image uploaded successfully.");

      closeAddForm();

      await fetchAboutImages();
    } catch (error) {
      console.error("Upload About Image Error:", error);

      alert("Unable to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  /* =====================================================
     DELETE IMAGE
  ===================================================== */

  const handleDelete = async (id) => {
    if (!id) {
      alert("Invalid image ID.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this image?",
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(id);

      const response = await fetch(
        `${BASE_URL}/about.php?action=delete&id=${id}`,
        {
          method: "DELETE",
        },
      );

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid Delete Response:", text);

        alert("Invalid server response.");
        return;
      }

      if (!result.status) {
        alert(result.message || "Failed to delete image.");
        return;
      }

      alert("About image deleted successfully.");

      setImages((prev) =>
        prev.filter((item) => Number(item.id) !== Number(id)),
      );
    } catch (error) {
      console.error("Delete About Image Error:", error);

      alert("Unable to delete image. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <main className="about-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        className="about-page-header"
        data-aos="fade-down"
        data-aos-duration="700"
      >
        <div>
          <span className="about-page-label">ABOUT MANAGEMENT</span>

          <h1>About Images</h1>

          <p>Upload and manage images displayed on the About page.</p>
        </div>

        <button type="button" className="about-add-btn" onClick={openAddForm}>
          <FaPlus />
          Add Image
        </button>
      </div>

      {/* =====================================================
          IMAGE COUNT
      ===================================================== */}

      {!loading && (
        <div
          className="about-image-count"
          data-aos="fade-up"
          data-aos-delay="150"
        >
          <FaImage />

          <span>
            Total Images: <strong>{images.length}</strong>
          </span>
        </div>
      )}

      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading ? (
        <div className="about-loading">Loading images...</div>
      ) : images.length === 0 ? (
        /* =====================================================
           EMPTY
        ===================================================== */

        <div className="about-empty">
          <FaImage />

          <h3>No About Images</h3>

          <p>Add your first About page image.</p>

          <button type="button" className="about-add-btn" onClick={openAddForm}>
            <FaPlus />
            Add Image
          </button>
        </div>
      ) : (
        /* =====================================================
           IMAGE GRID
        ===================================================== */

        <div className="about-images">
          {images.map((item, index) => (
            <div
              className="about-image-card"
              key={item.id || index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              data-aos-duration="700"
            >
              <div className="about-image-wrapper">
                <img src={getImageUrl(item.image)} alt={`About ${index + 1}`} />

                {/* DELETE BUTTON */}

                <button
                  type="button"
                  className="about-delete-btn"
                  onClick={() => handleDelete(item.id)}
                  disabled={deletingId === item.id}
                  title="Delete Image"
                >
                  {deletingId === item.id ? "..." : <FaTrash />}
                </button>
              </div>

              <div className="about-image-footer">
                <span>Image {index + 1}</span>

                <button
                  type="button"
                  className="about-footer-delete"
                  onClick={() => handleDelete(item.id)}
                  disabled={deletingId === item.id}
                >
                  <FaTrash />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* =====================================================
          ADD IMAGE MODAL
      ===================================================== */}

      {showAddForm && (
        <div className="about-modal-overlay">
          <div className="about-modal">
            {/* MODAL HEADER */}

            <div className="about-modal-header">
              <div>
                <span>ABOUT MANAGEMENT</span>

                <h2>Add About Image</h2>
              </div>

              <button
                type="button"
                className="about-modal-close"
                onClick={closeAddForm}
                disabled={uploading}
              >
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <form className="about-upload-form" onSubmit={handleUpload}>
              {/* FILE */}

              <div className="about-upload-field">
                <label>
                  About Image
                  <span>*</span>
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileChange}
                />

                <small>JPG, JPEG, PNG or WEBP. Maximum 10 MB.</small>
              </div>

              {/* PREVIEW */}

              {preview && (
                <div className="about-preview">
                  <div className="about-preview-header">
                    <span>Image Preview</span>
                  </div>

                  <img src={preview} alt="Preview" />
                </div>
              )}

              {/* ACTIONS */}

              <div className="about-modal-actions">
                <button
                  type="button"
                  className="about-cancel-btn"
                  onClick={closeAddForm}
                  disabled={uploading}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="about-upload-btn"
                  disabled={uploading || !selectedFile}
                >
                  <FaUpload />

                  {uploading ? "Uploading..." : "Upload Image"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
